/**
 * useScheduleSend Hook
 *
 * Schedule message, list scheduled, reschedule, cancel via React Query.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { api, EmailScheduledMessage } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export function useScheduleSend(accountId: string | undefined) {
  const queryClient = useQueryClient();

  const queryResult = useQuery({
    queryKey: queryKeys.email.scheduled(accountId ?? ''),
    queryFn: async () => {
      if (!accountId) throw new Error('No account');
      return api.listScheduledEmails(accountId);
    },
    enabled: !!accountId,
  });

  const invalidate = useCallback(() => {
    if (!accountId) return;
    queryClient.invalidateQueries({ queryKey: queryKeys.email.scheduled(accountId) });
  }, [accountId, queryClient]);

  const schedule = useCallback(
    async (messageId: string, scheduledFor: string) => {
      if (!accountId) return;
      const result = await api.scheduleEmailSend({ accountId, messageId, scheduledFor });
      invalidate();
      return result;
    },
    [accountId, invalidate],
  );

  const reschedule = useCallback(
    async (scheduleId: string, scheduledFor: string) => {
      if (!accountId) return;
      const result = await api.rescheduleEmail(scheduleId, accountId, scheduledFor);
      invalidate();
      return result;
    },
    [accountId, invalidate],
  );

  const cancel = useCallback(
    async (scheduleId: string) => {
      if (!accountId) return;
      const result = await api.cancelScheduledEmail(scheduleId, accountId);
      invalidate();
      return result;
    },
    [accountId, invalidate],
  );

  return {
    scheduled: queryResult.data?.scheduled ?? ([] as EmailScheduledMessage[]),
    scheduledCount: queryResult.data?.scheduled?.length ?? 0,
    loading: queryResult.isLoading,
    schedule,
    reschedule,
    cancel,
    refresh: queryResult.refetch,
  };
}
